/**
 * Pure FWP formulas the battle rules (./battle.ts) use, ported line by line from src/game/FWP.ts:
 *  - perkEffect      = FWP.get_perk_effect (every perk, table form of the original if/switch chain);
 *  - skillMulti      = FWP.get_skill_multi (difficulty multipliers, reads player.settings.skill);
 *  - hardcoreMulti   = FWP.get_hardcore_multi (hardcore reward multiplier);
 *  - ratingInc       = FWP.get_rating_inc (daily win streak rating bonus);
 *  - tiredEffect     = FWP.get_tired_effect; affectedSkills = FWP.get_affected_skills (stims, tiredness, implant);
 *  - battleTeamDef   = the TEAMS_DEF lookup of prepare_game / init_game / end_game_fn.
 * tests/rules/battle-formulas.test.ts compares every function with the game's own method.
 *
 * They are shared by other rule groups too (e.g. merchant, luck or gsearch perks read perkEffect): import them from
 * here instead of porting get_perk_effect again.
 */
import { keys } from '../as3';
import type { GameDefs } from '../defs';

/**
 * Perks whose effect is `switch (level) { case 1: a; case 2: b; case 3: c }` in get_perk_effect (FWP.ts
 * get_perk_effect; values copied from the original, checked against it by the tests).
 */
const PERK_LEVEL_EFFECTS: Readonly<Record<string, readonly [number, number, number]>> = {
  dragonball: [1.5, 2, 3],
  builder: [1.15, 1.3, 1.5],
  champion: [1.1, 1.15, 1.2],
  education: [2, 3, 5],
  fire_punch: [-4.8, -5.2, -5.6],
  engineer: [350, 450, 600],
  miner: [2, 1, 0],
  crafting: [5, 7, 9],
  electrician: [0.1, 0.2, 0.3],
  merchant: [0.95, 0.9, 0.85],
  medic: [1.05, 1.1, 1.15],
  para_res: [0.7, 0.5, 0.3],
  luck: [0.02, 0.07, 0.14],
  luck2: [0.02, 0.07, 0.14],
  angel: [0.1, 0.25, 0.5],
  fire_res: [0.7, 0.5, 0.3],
  legioner: [1, 2, 4],
  comandeer: [50, 100, 150],
  poison_res: [-1, -2, -3],
  flyer: [10, 20, 30],
  sprotsman: [1.05, 1.15, 1.3],
  heavy_guy: [0.9, 0.8, 0.1],
  accuracy: [5, 2, 1],
  best_crit: [0.1, 0.25, 0.5],
  stims: [1, 2, 3],
  fast_temp: [1, 2, 3],
  thinker: [15, 30, 45],
  soft_land: [1.2, 1.5, 100],
  regeneration: [10, 20, 30],
  vampire: [0.4, 0.8, 1.5],
  bar: [0.1, 0.3, 0.5],
  more_exp: [1.05, 1.1, 1.15],
  up_pistols: [0.1, 0.25, 0.4],
  up_rifles: [0.1, 0.15, 0.3],
  up_miniguns: [0.1, 0.15, 0.3],
  less_fail: [0.85, 0.7, 0.5],
  more_fail: [0.08, 0.15, 0.25],
  less_tired: [1, 2, 3],
  gsearch: [0.7, 0.55, 0.3],
};

/** as3.str: null/undefined -> null, anything else -> String(v). */
function str(v: unknown): string | null {
  return v == null ? null : String(v);
}

/** AS3 `switch (level)` over the cases 1, 2, 3 (strict equality with the Number()-coerced level). */
function levelCase(level: number): 0 | 1 | 2 | -1 {
  if (level === 1) return 0;
  if (level === 2) return 1;
  if (level === 3) return 2;
  return -1;
}

/**
 * FWP.get_perk_effect(name, level, extra): the effect value of perk `name` at `level` (0 when the perk or the level
 * is unknown). `extra` matters only for big_guns (the weapon class: < 5 penalty, >= 5 bonus).
 */
export function perkEffect(name: unknown, level: unknown, extra: unknown = ''): number {
  const n = str(name);
  const l = Number(level);
  const x = str(extra);
  if (n === 'laser_sights' || n === 'surprise') return l;
  if (n === 'dmg_thr') return l * 10;
  if (n === 'big_guns') {
    const c = levelCase(l);
    if (Number(x) < 5 && c !== -1) return [-0.1, -0.2, -0.3][c];
    if (Number(x) >= 5 && c !== -1) return [0.1, 0.15, 0.2][c];
    return 0;
  }
  if (n !== null && Object.prototype.hasOwnProperty.call(PERK_LEVEL_EFFECTS, n)) {
    const c = levelCase(l);
    if (c !== -1) return PERK_LEVEL_EFFECTS[n][c];
  }
  return 0;
}

/** Names of every perk get_perk_effect knows (for tests and tools). */
export const PERK_EFFECT_NAMES: readonly string[] = [...Object.keys(PERK_LEVEL_EFFECTS), 'laser_sights', 'surprise', 'dmg_thr', 'big_guns'];

/**
 * FWP.get_skill_multi(kind) for a player whose settings.skill is `skill` (0..4, +10 in hardcore mode): the reward
 * multiplier ('exp', 'gold'), the enemy bonus ('attack', 'protect': added) or the enemy health multiplier
 * ('health'). Unknown kinds and difficulty values give 1 (the original's fall-through).
 */
export function skillMulti(skill: unknown, kind: unknown): number {
  const k = str(kind);
  let s: any = skill;
  if (s >= 10) s -= 10;
  if (k === 'exp' || k === 'gold') {
    switch (s) {
      case 0:
        return 1;
      case 1:
        return 1.15;
      case 2:
        return 1.3;
      case 3:
        return 1.6;
      case 4:
        return 2;
    }
  } else if (k === 'attack' || k === 'protect') {
    switch (s) {
      case 0:
        return 0;
      case 1:
        return 60;
      case 2:
        return 120;
      case 3:
        return 180;
      case 4:
        return 250;
    }
  } else if (k === 'health') {
    switch (s) {
      case 0:
        return 1;
      case 1:
        return 1.3;
      case 2:
        return 1.6;
      case 3:
        return 1.9;
      case 4:
        return 2.4;
    }
  }
  return 1;
}

/**
 * FWP.get_hardcore_multi(stage, max): 1 + (stage / max)^2 * 0.75 in hardcore mode (settings.skill >= 10), else 1.
 * end_game_fn passes the stage and the mode's *_max before the win increments it.
 */
export function hardcoreMulti(skill: unknown, stage: unknown, max: unknown): number {
  const a = Number(stage);
  const b = Number(max);
  let m = 1;
  if ((skill as any) >= 10) m = 1 + Math.pow(a / b, 2) * 0.75;
  return Number(m);
}

/** FWP.get_rating_inc(todayWins, yesterdayWins): extra rating for the 10th, 25th, 50th, 100th and 300th win of a day. */
export function ratingInc(todayWins: unknown, yesterdayWins: unknown): number {
  const t = Number(todayWins);
  const y = Number(yesterdayWins);
  const pick = (v: readonly [number, number, number, number, number, number]) => {
    if (y >= 300) return v[0];
    if (y >= 100) return v[1];
    if (y >= 50) return v[2];
    if (y >= 25) return v[3];
    if (y >= 10) return v[4];
    return v[5];
  };
  if (t == 10) return pick([30, 20, 15, 10, 8, 5]);
  if (t == 25) return pick([50, 25, 20, 15, 12, 10]);
  if (t == 50) return pick([75, 35, 30, 25, 20, 15]);
  if (t == 100) return pick([100, 60, 50, 40, 25, 20]);
  if (t == 300) return pick([500, 150, 100, 75, 60, 50]);
  return 0;
}

export interface TiredEffect {
  attack: number;
  protect: number;
  health: number;
}

/** FWP.get_tired_effect(tired): the attack/protect penalty and health factor of the highest TIRED_EFFECTS threshold <= tired. */
export function tiredEffect(defs: Pick<GameDefs, 'TIRED_EFFECTS'>, tired: unknown): TiredEffect {
  const t = Number(tired);
  const out: TiredEffect = { attack: 0, protect: 0, health: 1 };
  for (let i = 6; i >= 0; i--) {
    const e = defs.TIRED_EFFECTS[i];
    if (e.threshold <= t) {
      out.attack = e.attack;
      out.protect = e.protect;
      out.health = e.health;
      return out;
    }
  }
  return out;
}

/** Input of get_affected_skills (the object init_game and new_interf.render build from the player). */
export interface SkillsInput {
  attack: number;
  protect: number;
  /** 100 + 50 * player.health. */
  health: number;
  worms?: number;
  tired: number;
  stim_to_fight: Record<string, number>;
  /** player.cur_impl (IMPL_DEF id, 0 = none). */
  impl: number;
}

export interface AffectedSkills {
  attack: number;
  protect: number;
  health: number;
  /** tired + the stims' tiredness, at least 0. */
  tired: number;
  desc_attack: { stim: number; tired: number; impl: number };
  desc_protect: { stim: number; tired: number; impl: number };
  desc_health: { stim: number; tired: number; impl: number };
}

/**
 * FWP.get_affected_skills: the worm skills after the stims taken into the battle, the tiredness penalty (of tired +
 * the stims' own tiredness) and the implant. Every key of stim_to_fight must be a STIM_DEF id (the original crashes
 * otherwise; callers check it first).
 */
export function affectedSkills(defs: Pick<GameDefs, 'STIM_DEF' | 'IMPL_DEF' | 'TIRED_EFFECTS'>, p: SkillsInput): AffectedSkills {
  const s = { attack: 0, protect: 0, health: 0, tired: 0 };
  for (const k of keys(p.stim_to_fight)) {
    const d = defs.STIM_DEF[k];
    s.attack += d.attack * p.stim_to_fight[k];
    s.protect += d.protect * p.stim_to_fight[k];
    s.health += d.health * p.stim_to_fight[k];
    s.tired += d.tired * p.stim_to_fight[k];
  }
  const te = tiredEffect(defs, p.tired + s.tired);
  const im = { attack: 0, protect: 0, health: 0 };
  for (const k of keys(defs.IMPL_DEF)) {
    if (defs.IMPL_DEF[k].id == p.impl) {
      im.attack = defs.IMPL_DEF[k].attack;
      im.protect = defs.IMPL_DEF[k].protect;
      im.health = defs.IMPL_DEF[k].health;
    }
  }
  let tired = p.tired + s.tired;
  if (tired < 0) tired = 0;
  return {
    attack: p.attack + s.attack + te.attack + im.attack,
    protect: p.protect + s.protect + te.protect + im.protect,
    health: Math.round((p.health + s.health) * te.health) + im.health,
    tired,
    desc_attack: { stim: s.attack, tired: te.attack, impl: im.attack },
    desc_protect: { stim: s.protect, tired: te.protect, impl: im.protect },
    desc_health: { stim: s.health, tired: te.health, impl: im.health },
  };
}

/** Battle modes with a TEAMS_DEF stage table ('just' battles use the lobby's barracks instead). */
export type StageMode = 'career' | 'missions' | 'survival' | 'boss' | 'assault';

/** TEAMS_DEF key prefix of a mode (missions are "mission<N>"). */
export const TEAMS_DEF_PREFIX: Readonly<Record<StageMode, string>> = {
  career: 'career',
  missions: 'mission',
  survival: 'survival',
  boss: 'boss',
  assault: 'assault',
};

/** TEAMS_DEF[prefix + id] as prepare_game / init_game / end_game_fn look it up (undefined when there is none). */
export function battleTeamDef(defs: Pick<GameDefs, 'TEAMS_DEF'>, mode: StageMode, id: number): any {
  const key = TEAMS_DEF_PREFIX[mode] + id;
  return Object.prototype.hasOwnProperty.call(defs.TEAMS_DEF, key) ? defs.TEAMS_DEF[key] : undefined;
}
